// M2V.io - Auth Store
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api'
import { setLocale, type Locale } from '@/i18n'

export interface User {
  id: number
  email: string
  display_name: string | null
  locale: string
  theme_pref: string
  created_at: string
  credits_balance: number
  // Director subscription
  has_director_subscription: boolean
  director_subscription_status: string | null
  director_subscription_end: string | null
  is_director: boolean
  // Stripe account (for billing portal access)
  has_stripe_account: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const credits = computed(() => user.value?.credits_balance ?? 0)
  const isDirector = computed(() => user.value?.is_director ?? false)
  const hasDirectorSubscription = computed(() => user.value?.has_director_subscription ?? false)
  const subscriptionStatus = computed(() => user.value?.director_subscription_status ?? null)
  const hasStripeAccount = computed(() => user.value?.has_stripe_account ?? false)

  // Initialize from localStorage
  async function init() {
    const storedToken = localStorage.getItem('m2v_token')
    if (storedToken) {
      token.value = storedToken
      try {
        await fetchUser()
      } catch {
        // Token invalid, clear it
        logout()
      }
    }
    initialized.value = true
  }

  // Fetch current user
  async function fetchUser() {
    if (!token.value) return
    
    loading.value = true
    try {
      const response = await api.get('/users/me')
      user.value = response.data
      
      // Apply user preferences
      if (user.value?.locale) {
        setLocale(user.value.locale as Locale)
      }
      if (user.value?.theme_pref) {
        document.documentElement.setAttribute('data-theme', user.value.theme_pref)
      }
    } finally {
      loading.value = false
    }
  }

  // Login with token (from OAuth callback)
  function setToken(newToken: string) {
    token.value = newToken
    localStorage.setItem('m2v_token', newToken)
  }

  // Dev login (local mode only)
  async function devLogin(email: string) {
    loading.value = true
    try {
      const response = await api.post(`/auth/dev-login?email=${encodeURIComponent(email)}`)
      const { access_token, user: userData } = response.data
      
      token.value = access_token
      user.value = userData
      localStorage.setItem('m2v_token', access_token)
      
      return true
    } catch (error) {
      console.error('Dev login failed:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  // Start OAuth flow
  async function startOAuth(provider: 'google' | 'facebook') {
    try {
      const response = await api.get(`/auth/${provider}/start`)
      window.location.href = response.data.auth_url
    } catch (error) {
      console.error('OAuth start failed:', error)
      throw error
    }
  }

  // Logout
  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem('m2v_token')
  }

  // Update user profile
  async function updateProfile(data: Partial<User>) {
    if (!token.value) return
    
    loading.value = true
    try {
      const response = await api.put('/users/me', data)
      user.value = response.data
      
      if (data.locale) {
        setLocale(data.locale as Locale)
      }
      if (data.theme_pref) {
        document.documentElement.setAttribute('data-theme', data.theme_pref)
      }
      
      return true
    } catch (error) {
      console.error('Profile update failed:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  // Refresh credits
  async function refreshCredits() {
    if (!token.value) return
    
    try {
      const response = await api.get('/users/me/wallet')
      if (user.value) {
        user.value.credits_balance = response.data.credits_balance
      }
    } catch (error) {
      console.error('Failed to refresh credits:', error)
    }
  }

  // Subscribe to Director plan
  async function subscribeDirector(successUrl?: string, cancelUrl?: string) {
    if (!token.value) return null
    
    try {
      const response = await api.post('/payments/subscription/checkout', {
        success_url: successUrl,
        cancel_url: cancelUrl
      })
      return response.data.checkout_url
    } catch (error) {
      console.error('Failed to create subscription checkout:', error)
      return null
    }
  }

  // Cancel Director subscription
  async function cancelSubscription() {
    if (!token.value) return false
    
    try {
      await api.post('/payments/subscription/cancel')
      await fetchUser() // Refresh user data
      return true
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
      return false
    }
  }

  // Reactivate Director subscription
  async function reactivateSubscription() {
    if (!token.value) return false
    
    try {
      await api.post('/payments/subscription/reactivate')
      await fetchUser() // Refresh user data
      return true
    } catch (error) {
      console.error('Failed to reactivate subscription:', error)
      return false
    }
  }

  // Get Stripe portal URL for managing subscription
  async function getSubscriptionPortalUrl() {
    if (!token.value) return null
    
    try {
      const response = await api.get('/payments/subscription/portal')
      return response.data.url
    } catch (error) {
      console.error('Failed to get subscription portal:', error)
      return null
    }
  }

  return {
    user,
    token,
    loading,
    initialized,
    isAuthenticated,
    credits,
    isDirector,
    hasDirectorSubscription,
    subscriptionStatus,
    hasStripeAccount,
    init,
    fetchUser,
    setToken,
    devLogin,
    startOAuth,
    logout,
    updateProfile,
    refreshCredits,
    subscribeDirector,
    cancelSubscription,
    reactivateSubscription,
    getSubscriptionPortalUrl
  }
})
